import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LoginScreen from './features/auth/login/LoginScreen';
import './assets/styles/global.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SignupScreen from './features/auth/signup/SignupScreen';
import LoadingScreen from './screens/LoadingScreen';
import { isAuthenticated, logout } from './services/authService';
import { AuthProvider } from './contexts/AuthContext';
import { UseCaseProvider } from './contexts/UseCaseContext';
import { LlmConfigProvider } from './contexts/LlmConfigContext';
import ChatbotScreen from './features/chatbotScreen/ChatbotScreen.js';
import TemplateDashboard from './features/templateDashboard/TemplateDashboard.js';
import ResponsibleAI from './features/responsibleAI/ResponsibleAI.js';
import SettingsScreen from './features/userSettings/Settings.js';
import SettingsPage from './features/settings/SettingsPage.js';
import CustomerGroupManagement from './features/customerGroupManagement/CustomerGroupManagement.js';
import GuardrailsManager from './features/guardrails/GuardrailsManager.js';
import UnauthorizedPage from './screens/UnauthorizedPage.js';
import AuthorPage from './features/authering/AuthorPage.js';
import CustomBuild from './features/customBuild/CustomBuild.js';
import Segmentation from './features/segmentation/Segmentation.js';
import MetadataExtraction from './features/metadataExtraction/MetadataExtraction.js';
import UHNWIObservatoryDashboard from './features/observatoryDashboard/UHNWIObservatoryDashboard.js';
import Dashboard2 from './features/preCallSummaryDashboard/index.js';
import ProcessTracker from './features/processTracker/ProcessTracker.js';
import { ColorService } from './features/colors';
import 'reactflow/dist/style.css';
import Test from './screens/Test.js';
import AgentWorkflow from './features/AgentWorkflow/AgentWorkflow.js';
import AgentMarketplace from './features/agentMarketplace/AgentMarketplace.js';
import ToolDetailPage from './features/agentMarketplace/components/ToolDetailPage.js';
import AddToolForm from './features/agentMarketplace/components/AddToolForm.js';
import AgentDetailPage from './features/agentMarketplace/components/AgentDetailPage.js';
import UserTemplateDashboard from './features/userTemplateDashboard/UserTemplateDashboard.js';
import TrainingSimulation from './features/training-simulation/TrainingSimulation.js';
import UiElementsTest from './screens/UiElementsTest.js';
import RendererTest from './screens/RendererTest.js';
import PromptInput from './features/promptInput/PromptInput.js';
import FinancialSentimentApp from './features/sentimentAnalysis/SentimentAnalysis';
import CustomerServiceDashboard from './features/customerServiceDashboard/CustomerServiceDashboard.js';
import CustomerServiceDashboardWithBot from './features/customerServiceDashboardWithBot/CustomerServiceDashboardWithBot.js';
import WorkspacePage from './pages/WorkspacePage.js';
import DataStudioPage from './pages/DataStudioPage.js';
import SourceExplorer from './features/analyzeData/SourceExplorer.js';
import NewSource from './features/analyzeData/NewSource.js';
import EditSource from './features/analyzeData/EditSource.js';
import DomainAnalysis from './features/analyzeData/DomainAnalysis.js';
import AIGovernancePage from './pages/AIGovernancePage.js';
import AdministrationPage from './pages/AdministrationPage.js';
import AppSettingsPage from './pages/AppSettingsPage.js';
import AppLayout from './components/nav/AppLayout.js';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

  useEffect(() => {
    const initializeApp = async () => {
      const userId = sessionStorage.getItem('user_id');
      setCurrentUserId(userId);
      setIsLoading(false);
    };

    initializeApp();
  }, []);

  useEffect(() => {
    const checkUserChange = async () => {
      const userId = sessionStorage.getItem('user_id');

      if (userId !== currentUserId) {

        setCurrentUserId(userId);

        if (userId) {
          try {
            ColorService.clearColorCache(userId);

            await ColorService.refreshUserColors(userId);

          } catch (error) {
            console.error('Error refreshing colors for new user:', error);

            ColorService.applyColors(ColorService.getDefaultColors());
            ColorService.applyConstantColors();
          }
        } else {
          ColorService.applyColors(ColorService.getDefaultColors());
          ColorService.applyConstantColors();
        }
      }
    };

    checkUserChange();
    const interval = setInterval(checkUserChange, 5000);

    return () => clearInterval(interval);
  }, [currentUserId]);



  const InactivityTracker = () => {
    const navigate = useNavigate();

    useEffect(() => {
      if (!isAuthenticated()) return;

      let inactivityTimer;

      const resetTimer = () => {
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }

        inactivityTimer = setTimeout(() => {
          logout();

          toast.info('You have been logged out due to inactivity.');

          navigate('/a3-login');
        }, INACTIVITY_TIMEOUT);
      };

      resetTimer();

      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

      events.forEach(event => {
        document.addEventListener(event, resetTimer);
      });

      return () => {
        clearTimeout(inactivityTimer);
        events.forEach(event => {
          document.removeEventListener(event, resetTimer);
        });
      };
    }, [navigate]);

    return null;
  };

  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated()) return <Navigate to="/a3-login" />;
    return <AppLayout>{children}</AppLayout>;
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthProvider>
      <LlmConfigProvider>
      <UseCaseProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <InactivityTracker />

          <div className="App">
            <Routes>
              <Route path="/a3-login" element={<LoginScreen />} />
              <Route path="/a3-signup" element={<SignupScreen />} />
              <Route path="/pre-call-summary" element={<ProtectedRoute><Dashboard2 /></ProtectedRoute>} />
              <Route path="/uhnwi-observatory" element={<ProtectedRoute><UHNWIObservatoryDashboard /></ProtectedRoute>} />
              <Route path="/home" element={<ProtectedRoute><ChatbotScreen /></ProtectedRoute>} />
              <Route path="/agent-workflow" element={<ProtectedRoute><AgentWorkflow /></ProtectedRoute>} />
              {/* <Route path="/home-new" element={<ProtectedRoute><ChatbotHome /></ProtectedRoute>} /> */}

              <Route path="/authoring" element={<ProtectedRoute><AuthorPage /></ProtectedRoute>} />
              <Route path="/custom-build" element={<ProtectedRoute><CustomBuild /></ProtectedRoute>} />
              <Route path="/marketplace" element={<ProtectedRoute><AgentMarketplace /></ProtectedRoute>} />
              <Route path="/tool/:id" element={<ProtectedRoute><ToolDetailPage /></ProtectedRoute>} />
              <Route path="/agent/:id" element={<ProtectedRoute><AgentDetailPage /></ProtectedRoute>} />
              <Route path="/add-tool" element={<ProtectedRoute><AddToolForm /></ProtectedRoute>} />
              <Route path="/simulation" element={<ProtectedRoute><TrainingSimulation /></ProtectedRoute>} />
              <Route path="/ui-elements-test" element={<ProtectedRoute><UiElementsTest /></ProtectedRoute>} />
              <Route path="/prompt-input" element={<ProtectedRoute><PromptInput /></ProtectedRoute>} />
              <Route path="/renderer-test" element={<ProtectedRoute><RendererTest /></ProtectedRoute>} />
              <Route path="/financial-sentiment" element={<ProtectedRoute><FinancialSentimentApp /></ProtectedRoute>} />
              <Route path="/customer-service" element={<ProtectedRoute><CustomerServiceDashboard /></ProtectedRoute>} />
              <Route path="/customer-service-bot" element={<ProtectedRoute><CustomerServiceDashboardWithBot /></ProtectedRoute>} />


              {/* Dynamic use case route - supports both ID and slug */}
              <Route path="/use-case/:id" element={<ProtectedRoute><TemplateDashboard /></ProtectedRoute>} />
              <Route path="/user-dashboard" element={<ProtectedRoute><UserTemplateDashboard /></ProtectedRoute>} />

              <Route path="/responsible-ai" element={<ProtectedRoute allowedRoles={['admin']}><ResponsibleAI /></ProtectedRoute>} />
              <Route path="/user-settings" element={<ProtectedRoute><SettingsScreen /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
              <Route path="/cust-group-management" element={<ProtectedRoute allowedRoles={['admin']}><CustomerGroupManagement /></ProtectedRoute>} />
              <Route path="/guardrail" element={<ProtectedRoute allowedRoles={['admin']}><GuardrailsManager /></ProtectedRoute>} />
              <Route path="/guardrail/:useCase" element={<ProtectedRoute allowedRoles={['admin']}><GuardrailsManager /></ProtectedRoute>} />
              <Route path="/segmentation" element={<ProtectedRoute allowedRoles={['admin']}><Segmentation /></ProtectedRoute>} />
              <Route path="/metadata-extraction" element={<ProtectedRoute allowedRoles={['admin']}><MetadataExtraction /></ProtectedRoute>} />
              <Route path='/process-tracker' element={<ProtectedRoute allowedRoles={['admin']}><ProcessTracker /></ProtectedRoute>} />
              <Route path="/workspace" element={<Navigate to="/workspace/custom-build" replace />} />
              <Route path="/workspace/custom-build" element={<ProtectedRoute><CustomBuild /></ProtectedRoute>} />
              <Route path="/workspace/services" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
              <Route path="/workspace/ddlc" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
              <Route path="/workspace/prompt-renderer" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
              <Route path="/data-studio" element={<Navigate to="/data-studio/segmentation" replace />} />
              <Route path="/data-studio/segmentation" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/cluster-analysis" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/metadata" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/analyze" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/query" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/knowledge-graph" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/ingest" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/doc-intelligence" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/explore" element={<ProtectedRoute allowedRoles={['admin']}><SourceExplorer /></ProtectedRoute>} />
              <Route path="/sources/new" element={<ProtectedRoute allowedRoles={['admin']}><NewSource /></ProtectedRoute>} />
              <Route path="/sources/edit" element={<ProtectedRoute allowedRoles={['admin']}><EditSource /></ProtectedRoute>} />
              <Route path="/domain-analysis" element={<ProtectedRoute allowedRoles={['admin']}><DomainAnalysis /></ProtectedRoute>} />
              <Route path="/ai-governance" element={<Navigate to="/ai-governance/responsible-ai" replace />} />
              <Route path="/ai-governance/responsible-ai" element={<ProtectedRoute allowedRoles={['admin']}><AIGovernancePage /></ProtectedRoute>} />
              <Route path="/ai-governance/responsible-ai/:tab" element={<ProtectedRoute allowedRoles={['admin']}><AIGovernancePage /></ProtectedRoute>} />
              <Route path="/ai-governance/agent-lineage" element={<ProtectedRoute allowedRoles={['admin']}><AIGovernancePage /></ProtectedRoute>} />
              <Route path="/administration" element={<Navigate to="/administration/user-management" replace />} />
              <Route path="/administration/user-management" element={<ProtectedRoute allowedRoles={['admin']}><AdministrationPage /></ProtectedRoute>} />
              <Route path="/administration/pool-management" element={<ProtectedRoute allowedRoles={['admin']}><AdministrationPage /></ProtectedRoute>} />
              <Route path="/app-settings" element={<ProtectedRoute><AppSettingsPage /></ProtectedRoute>} />
              <Route path="/data-studio/governance-intake" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/transfer" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />
              <Route path="/data-studio/investigate" element={<ProtectedRoute allowedRoles={['admin']}><DataStudioPage /></ProtectedRoute>} />


              <Route path="/" element={<Navigate to="/a3-login" />} />
            </Routes>
          </div>
        </Router>
        <ToastContainer position="top-right" autoClose={3000} />
      </UseCaseProvider>
      </LlmConfigProvider>
    </AuthProvider>
  );
}

export default App;